import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { realtime, type RealtimeClient, type RtEvent, type RtStatus, type TopicView } from './client';

const NONE: TopicView = { data: undefined, ready: false, stale: false, error: null, events: [], seq: 0 };

/**
 * Live state of a realtime topic (snapshot + deltas). Pass null to subscribe to nothing.
 *   const { data, stale } = useTopic<DashboardState>('dashboard');
 */
export function useTopic<T = any>(topic: string | null | undefined, client: RealtimeClient = realtime): TopicView<T> {
  const subscribe = useCallback((cb: () => void) => (topic ? client.subscribe(topic, cb) : () => {}), [topic, client]);
  const get = useCallback(() => (topic ? client.view<T>(topic) : (NONE as TopicView<T>)), [topic, client]);
  return useSyncExternalStore(subscribe, get, get);
}

/** Calls `handler` for every `event` message of an event topic (alerts, cases) while mounted. */
export function useTopicEvents<T = any>(
  topic: string | null | undefined,
  handler: (e: RtEvent<T>) => void,
  client: RealtimeClient = realtime,
) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    if (!topic) return;
    const unsub = client.subscribe(topic, () => {});
    const off = client.onEvent(topic, (e) => ref.current(e as RtEvent<T>));
    return () => {
      off();
      unsub();
    };
  }, [topic, client]);
}

/** Connection status of the realtime client. */
export function useRealtimeStatus(client: RealtimeClient = realtime): RtStatus {
  return useSyncExternalStore(
    (cb) => client.onStatus(cb),
    () => client.getStatus(),
    () => client.getStatus(),
  );
}
